import { Link } from '~/router';

const LostCarAnimation = () => {
  return (
    <div className="mb-8 select-none" aria-hidden="true">
      <style>{`
        @keyframes lost-wander {
          0%   { transform: translateX(-100px) scaleX(1); }
          25%  { transform: translateX(70px) scaleX(1); }
          30%  { transform: translateX(70px) scaleX(-1); }
          55%  { transform: translateX(-100px) scaleX(-1); }
          60%  { transform: translateX(-100px) scaleX(1); }
          100% { transform: translateX(-100px) scaleX(1); }
        }
        @keyframes lost-question-pop {
          0%, 15%, 85%, 100% { opacity: 0; transform: translateY(6px) scale(0.7); }
          25%, 50%           { opacity: 1; transform: translateY(-4px) scale(1); }
          70%                { opacity: 0; transform: translateY(-12px) scale(0.9); }
        }
        @keyframes lost-sign-sway {
          0%, 100% { transform: rotate(-3deg); }
          50%      { transform: rotate(3deg); }
        }
        @keyframes lost-cloud-drift {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-320px); }
        }
        .lost-car { animation: lost-wander 6s ease-in-out infinite; }
        .lost-question { animation: lost-question-pop 6s ease-in-out infinite; }
        .lost-sign { animation: lost-sign-sway 3s ease-in-out infinite; transform-origin: bottom center; }
        .lost-cloud { animation: lost-cloud-drift 18s linear infinite; }
      `}</style>

      <div
        className="relative mx-auto overflow-hidden rounded-lg"
        style={{
          height: 176,
          width: 320,
          background: 'linear-gradient(to bottom, #f0f9ff, #ffffff)'
        }}
      >
        {/* Drifting clouds */}
        <div
          className="lost-cloud absolute top-3 left-0 flex"
          style={{ width: 640, gap: 80 }}
        >
          <div className="w-12 h-3 bg-white rounded-full opacity-80" />
          <div className="w-16 h-4 bg-white rounded-full opacity-70" />
          <div className="w-10 h-3 bg-white rounded-full opacity-80" />
          <div className="w-14 h-3 bg-white rounded-full opacity-75" />
          <div className="w-12 h-3 bg-white rounded-full opacity-80" />
        </div>

        {/* Road */}
        <div className="absolute bottom-4 left-0 right-0 h-0.5 bg-gray-300" />
        <div className="absolute bottom-3 left-0 right-0 flex justify-around">
          {Array.from({ length: 10 }).map((_, i) => (
            <div key={i} className="w-4 h-0.5 bg-gray-200" />
          ))}
        </div>

        {/* 404 signpost */}
        <div className="lost-sign absolute bottom-4 left-6">
          <div className="absolute bottom-0 left-3 w-1 h-14 bg-amber-700" />
          <div className="absolute bottom-10 left-0 px-2 py-1 bg-amber-500 border-2 border-amber-700 rounded text-white text-[10px] font-bold shadow whitespace-nowrap">
            404
          </div>
        </div>

        {/* Question mark hovering — sibling of car so it doesn't mirror with scaleX(-1) */}
        <div
          className="absolute"
          style={{ bottom: 56, left: '50%', marginLeft: -6 }}
        >
          <div className="lost-question text-blue-500 font-bold text-2xl">
            ?
          </div>
        </div>

        {/* Car */}
        <div
          className="lost-car absolute bottom-4"
          style={{ left: '50%' }}
        >
          <svg width="56" height="32" viewBox="0 0 56 32">
            {/* Body */}
            <rect
              x="2"
              y="13"
              width="52"
              height="13"
              rx="3"
              fill="#3b82f6"
            />
            {/* Roof */}
            <path
              d="M14 13 L18 4 L36 4 L42 13"
              fill="#60a5fa"
              stroke="#3b82f6"
              strokeWidth="1"
            />
            {/* Windows */}
            <path d="M19 5 L16 12 L26 12 L26 5 Z" fill="#bfdbfe" />
            <path d="M28 5 L28 12 L40 12 L36 5 Z" fill="#bfdbfe" />
            {/* Headlight */}
            <rect x="51" y="16" width="3" height="4" rx="1" fill="#fbbf24" />
            {/* Tail light */}
            <rect x="1" y="16" width="2" height="4" rx="1" fill="#ef4444" />
            {/* Front wheel */}
            <g>
              <circle cx="40" cy="26" r="5" fill="#374151" />
              <circle cx="40" cy="26" r="2" fill="#9ca3af" />
              <line
                x1="40"
                y1="22"
                x2="40"
                y2="30"
                stroke="#6b7280"
                strokeWidth="0.5"
              />
              <line
                x1="36"
                y1="26"
                x2="44"
                y2="26"
                stroke="#6b7280"
                strokeWidth="0.5"
              />
            </g>
            {/* Rear wheel */}
            <g>
              <circle cx="14" cy="26" r="5" fill="#374151" />
              <circle cx="14" cy="26" r="2" fill="#9ca3af" />
              <line
                x1="14"
                y1="22"
                x2="14"
                y2="30"
                stroke="#6b7280"
                strokeWidth="0.5"
              />
              <line
                x1="10"
                y1="26"
                x2="18"
                y2="26"
                stroke="#6b7280"
                strokeWidth="0.5"
              />
            </g>
          </svg>
        </div>
      </div>
    </div>
  );
};

export default function NotFoundPage() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-md rounded-lg bg-white p-8 shadow-lg text-center">
        <LostCarAnimation />

        <h1 className="mb-2 text-3xl font-bold text-gray-900">
          Page not found
        </h1>

        <p className="mb-6 text-gray-600">
          Looks like this page took a wrong turn. Our little car is searching
          high and low, but this road leads nowhere.
        </p>

        <Link
          to="/dashboard"
          className="inline-block rounded-md bg-blue-500 px-6 py-2 font-semibold text-white shadow hover:bg-blue-600 transition-colors"
        >
          Back to dashboard
        </Link>
      </div>
    </div>
  );
}
